'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { PauseSolidXs } from '@lichtblick/photon/icons/symbols/pause-solid-xs';
import { PlayXs } from '@lichtblick/photon/icons/symbols/play-xs';
import classNames from 'classnames';
import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LogoCarouselType } from '../../types/storyblok';
import { AImage } from '../AImage';
import styles from './logo-carousel.module.css';

export const LogoCarousel: FC<LogoCarouselType> = ({ borderBottom, borderTop, logos }) => {
  const viewRef = useRef<HTMLDivElement>(null);
  const [looping, setLooping] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [requestedPlaying, setRequestedPlaying] = useState<boolean>();
  const playing = requestedPlaying ?? !reducedMotion;

  useEffect(() => {
    const motionPreferenceObserver = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleMotionPreference = () => setReducedMotion(motionPreferenceObserver.matches);

    handleMotionPreference();
    motionPreferenceObserver.addEventListener('change', handleMotionPreference);

    return () => {
      motionPreferenceObserver.removeEventListener('change', handleMotionPreference);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: must be triggered on changing `logos`
  useLayoutEffect(() => {
    const determineLooping = () =>
      setLooping(
        viewRef.current !== null &&
          viewRef.current.scrollWidth >
            viewRef.current.clientWidth * (viewRef.current.classList.contains(styles.looping) ? 2 : 1),
      );

    determineLooping();
    window.addEventListener('resize', determineLooping);

    return () => {
      window.removeEventListener('resize', determineLooping);
    };
  }, [logos]);

  return (
    <div className={styles.container} style={{ '--item-count': logos.length } as any}>
      {borderTop && <hr />}
      <div className={styles.content}>
        <div className={classNames(styles.view, looping && styles.looping, playing && styles.playing)} ref={viewRef}>
          <ul className={styles.list}>
            {logos.map((item) => (
              <li key={item._uid}>
                <AImage {...item} />
              </li>
            ))}
            {looping &&
              logos.map((item) => (
                <li aria-hidden className={styles.copy} key={`${item._uid}_copy`}>
                  <AImage {...item} />
                </li>
              ))}
          </ul>
        </div>
        {looping && (
          <div className={styles['play-button']}>
            <BoxCTA
              ariaLabel={playing ? 'Logo-Karusell anhalten' : 'Logo-Karusell starten'}
              as="button"
              leftSlot={playing ? <PauseSolidXs /> : <PlayXs />}
              onClick={() => setRequestedPlaying(!playing)}
              size="small"
              type="button"
              variant="neutral-outline"
            />
          </div>
        )}
      </div>
      {borderBottom && <hr />}
    </div>
  );
};
